import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Stack,
  CircularProgress,
  Tooltip,
  Divider,
  Alert
} from '@mui/material';

import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import api from '../../api/axios';
import toast from 'react-hot-toast';

const HRDocumentApproval = () => {
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [documents, setDocuments] = useState([]);

  const [branchId, setBranchId] = useState('');
  const [employeeId, setEmployeeId] = useState('');

  const [remarks, setRemarks] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /* ================= LOAD BRANCHES ================= */
  useEffect(() => {
    api.get('/branches')
      .then(res => setBranches(res.data))
      .catch(() => toast.error('Failed to load branches'));
  }, []);

  /* ================= LOAD EMPLOYEES ================= */
  useEffect(() => {
    if (!branchId) return;

    setEmployees([]);
    setEmployeeId('');
    setDocuments([]);

    api.get(`/hr/branches/${branchId}/employees`)
      .then(res => setEmployees(res.data))
      .catch(() => toast.error('Failed to load employees'));
  }, [branchId]);

  /* ================= LOAD DOCUMENTS ================= */
  useEffect(() => {
    if (!employeeId) return;

    setLoading(true);
    setError('');

    api.get(`/documents/employee/${employeeId}`)
      .then(res => setDocuments(res.data))
      .catch(() => setError('Failed to load documents'))
      .finally(() => setLoading(false));
  }, [employeeId]);

  /* ================= ACTIONS ================= */

  const reloadDocuments = async () => {
    const res = await api.get(`/documents/employee/${employeeId}`);
    setDocuments(res.data);
  };

  const review = async (docId, status) => {
    try {
      await api.post(`/documents/review/${docId}`, null, {
        params: {
          status,
          remarks: remarks[docId] || ''
        }
      });

      toast.success(`Document ${status}`);
      reloadDocuments();
    } catch {
      toast.error('Action failed');
    }
  };

  const download = async (docId, fileName) => {
    try {
      const res = await api.get(`/documents/download/${docId}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
    } catch {
      toast.error('Download failed');
    }
  };

  const statusColor = (status) => {
    if (status === 'APPROVED') return 'success';
    if (status === 'REJECTED') return 'error';
    return 'warning';
  };

  /* ================= UI ================= */

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" fontWeight={900} gutterBottom>
        Document Approval Center
      </Typography>
      <Typography color="text.secondary" mb={3}>
        Review, approve or reject employee documents
      </Typography>

      {/* ================= FILTERS ================= */}
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Stack spacing={2}>
          <TextField
            select
            fullWidth
            label="Branch"
            value={branchId}
            onChange={e => setBranchId(e.target.value)}
          >
            {branches.map(b => (
              <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
            ))}
          </TextField>

          {branchId && (
            <TextField
              select
              fullWidth
              label="Employee"
              value={employeeId}
              onChange={e => setEmployeeId(e.target.value)}
            >
              {employees.map(e => (
                <MenuItem key={e.id} value={e.id}>
                  {e.name} ({e.employeeCode})
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      </Paper>

      {/* ================= DOCUMENT TABLE ================= */}
      {employeeId && (
        <Paper sx={{ mt: 4, borderRadius: 3 }}>
          <Divider />
          {loading && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {!loading && (
            <Table>
              <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                <TableRow>
                  <TableCell><b>Type</b></TableCell>
                  <TableCell><b>File</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                  <TableCell><b>HR Remarks</b></TableCell>
                  <TableCell align="right"><b>Actions</b></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {documents.map(doc => (
                  <TableRow key={doc.id} hover>
                    <TableCell>{doc.docType}</TableCell>
                    <TableCell>{doc.originalFileName}</TableCell>

                    <TableCell>
                      <Chip
                        label={doc.status}
                        color={statusColor(doc.status)}
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Remarks"
                        value={remarks[doc.id] ?? doc.hrRemarks ?? ''}
                        onChange={e =>
                          setRemarks({
                            ...remarks,
                            [doc.id]: e.target.value
                          })
                        }
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Tooltip title="Approve">
                        <IconButton
                          color="success"
                          onClick={() => review(doc.id, 'APPROVED')}
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Reject">
                        <IconButton
                          color="error"
                          onClick={() => review(doc.id, 'REJECTED')}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Download">
                        <IconButton
                          onClick={() =>
                            download(doc.id, doc.originalFileName)
                          }
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}

                {documents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No documents uploaded
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default HRDocumentApproval;
